import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile as firebaseUpdateProfile
} from 'firebase/auth';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

// Verify Firebase config is loaded
if (!firebaseConfig.apiKey) {
  console.warn('Firebase configuration is missing. Please check your .env file.');
}

class FirebaseService {
  /**
   * Sign in with email and password
   */
  async signInWithEmail(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();
      return { user: userCredential.user, idToken };
    } catch (error) {
      console.error('Firebase signInWithEmail error:', error);
      const handledError = this.handleFirebaseError(error);
      console.error('Handled error:', handledError);
      throw handledError;
    }
  }

  /**
   * Sign in with Google
   */
  async signInWithGoogle() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      return { user: result.user, idToken };
    } catch (error) {
      throw this.handleFirebaseError(error);
    }
  }

  /**
   * Sign out
   */
  async signOut() {
    try {
      await firebaseSignOut(auth);
      localStorage.removeItem('firebaseToken');
    } catch (error) {
      throw this.handleFirebaseError(error);
    }
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return auth.currentUser;
  }

  /**
   * Get ID token
   */
  async getIdToken(forceRefresh = false) {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    return await user.getIdToken(forceRefresh);
  }

  /**
   * Listen to auth state changes
   */
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, callback);
  }

  /**
   * Send password reset email
   */
  async sendPasswordReset(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      throw this.handleFirebaseError(error);
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(profileData) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user is currently signed in');
      }
      await firebaseUpdateProfile(user, profileData);
      return { success: true };
    } catch (error) {
      throw this.handleFirebaseError(error);
    }
  }

  /**
   * Handle Firebase errors and convert to user-friendly messages
   */
  handleFirebaseError(error) {
    // Handle different error formats
    const errorCode = error?.code || error?.error?.code || '';
    const errorMessage = error?.message || error?.error?.message || '';
    
    const errorMessages = {
      'auth/user-not-found': 'Wrong email or password. Please check your credentials and try again.',
      'auth/wrong-password': 'Wrong email or password. Please check your credentials and try again.',
      'auth/invalid-credential': 'Wrong email or password. Please check your credentials and try again.',
      'auth/invalid-email': 'Invalid email address.',
      'auth/email-already-in-use': 'This email is already registered.',
      'auth/weak-password': 'Password should be at least 6 characters.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
      'auth/network-request-failed': 'Network error. Please check your connection.',
      'auth/popup-closed-by-user': 'Sign-in popup was closed.',
      'auth/cancelled-popup-request': 'Only one popup request is allowed at a time.',
    };

    // For authentication errors (wrong password, user not found, invalid credential)
    // Show a generic message to avoid revealing if email exists
    const authErrors = ['auth/user-not-found', 'auth/wrong-password', 'auth/invalid-credential'];
    
    // Check if it's an authentication error
    if (authErrors.includes(errorCode)) {
      const authError = new Error('Wrong email or password. Please check your credentials and try again.');
      authError.code = errorCode;
      authError.message = 'Wrong email or password. Please check your credentials and try again.';
      authError.originalError = error;
      return authError;
    }

    // For other errors, return a proper Error object with the message
    const message = errorMessages[errorCode] || errorMessage || 'An error occurred during authentication.';
    const customError = new Error(message);
    customError.code = errorCode;
    customError.originalError = error;
    return customError;
  }
}

export const firebaseService = new FirebaseService();
export { auth, googleProvider };
export default firebaseService;

